<template>
    <div class="login-page">
        <!-- 启动动画 -->
        <div class="splash-screen" v-if="showSplash">
            <div class="splash-bg">
                <div class="splash-circle splash-circle-1"></div>
                <div class="splash-circle splash-circle-2"></div>
                <div class="splash-circle splash-circle-3"></div>
                <div class="splash-circle splash-circle-4"></div>
                <div class="splash-circle splash-circle-5"></div>
            </div>
            <div class="splash-logo">
                <span class="logo-sub">AI疗愈助手</span>
            </div>
            <div class="splash-loading">
                <div class="loading-dot"></div>
                <div class="loading-dot"></div>
                <div class="loading-dot"></div>
            </div>
        </div>

        <!-- 登录区域 -->
        <div class="login-container" v-else>
            <!-- 背景装饰 -->
            <div class="bg-decoration">
                <div class="bg-circle circle-1"></div>
                <div class="bg-circle circle-2"></div>
                <div class="bg-circle circle-3"></div>
                <div class="bg-circle circle-4"></div>
            </div>

            <!-- 登录卡片 -->
            <div class="login-card">
                <div class="card-header">
                    <div class="brand-icon">
                        <span class="icon-heart">♥</span>
                    </div>
                    <span class="card-title">欢迎回来</span>
                    <span class="card-desc">使用手机号快速登录</span>
                </div>

                <div class="card-body">
                    <!-- 手动输入 -->
                    <div class="manual-input">
                        <div class="input-wrapper">
                            <span class="input-prefix">+86</span>
                            <input
                                class="phone-input"
                                type="number"
                                v-model="phoneNumber"
                                placeholder="请输入手机号"
                                maxlength="11"
                            />
                        </div>
                        <button
                            class="submit-btn"
                            :class="{ disabled: !canLogin, loading: isLoggingIn }"
                            :disabled="!canLogin || isLoggingIn"
                            @click="handleLogin"
                        >
                            <span>{{ isLoggingIn ? '登录中...' : '登录' }}</span>
                        </button>
                    </div>
                </div>

                <div class="card-footer">
                    <span class="agreement-text">登录即表示同意</span>
                    <span class="agreement-link" @click="showAgreement('user')">《用户协议》</span>
                    <span class="agreement-text">和</span>
                    <span class="agreement-link" @click="showAgreement('privacy')">
                        《隐私政策》
                    </span>
                </div>
            </div>
        </div>

        <!-- 协议弹窗 -->
        <div class="modal-mask" v-if="showAgreementModal" @click="showAgreementModal = false">
            <div class="modal-content" @click.stop>
                <div class="modal-header">
                    <span class="modal-title">{{ agreementTitle }}</span>
                    <span class="modal-close" @click="showAgreementModal = false">×</span>
                </div>
                <!-- 可滚动协议内容（对应 uniapp scroll-view scroll-y） -->
                <div class="modal-body">
                    <span class="modal-text">{{ agreementContent }}</span>
                </div>
            </div>
        </div>

        <!-- 登录后选择弹窗 -->
        <div class="modal-mask assessment-mask" v-if="showAssessmentModal">
            <div class="assessment-modal">
                <div class="assessment-header">
                    <span class="assessment-title">欢迎回来</span>
                    <span class="assessment-desc">您希望以哪种方式开始？</span>
                </div>
                <div class="assessment-body">
                    <div class="assessment-btn primary" @click="goToChat">
                        <span class="btn-main">直接聊天</span>
                        <span class="btn-sub">与暖暖老师自由对话</span>
                    </div>
                    <div class="assessment-btn secondary" @click="goToAssessment">
                        <span class="btn-main">心理评估</span>
                        <span class="btn-sub">完成问卷获得专业分析</span>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { request } from '@/utils/request.js';
import { getStorage, setStorage, removeStorage } from '@/utils/storage.js';

export default {
    data() {
        return {
            showSplash: true, // 是否显示启动动画
            phoneNumber: '', // 手机号输入
            isLoggingIn: false, // 是否正在登录
            showAgreementModal: false, // 是否显示协议弹窗
            agreementTitle: '', // 协议标题
            agreementContent: '', // 协议内容
            showAssessmentModal: false, // 是否显示二筛评估弹窗
            pendingUserData: null, // 待处理的用户数据
            hasCompletedAssessment: false, // 是否已完成二筛评估
        };
    },
    computed: {
        // 手机号格式是否合法
        canLogin() {
            const phone = this.phoneNumber.trim();
            return /^1[3-9]\d{9}$/.test(phone);
        },
    },
    created() {
        // 启动动画1秒后消失，同时校验 token
        setTimeout(() => {
            this.showSplash = false;
            this.checkToken();
        }, 1000);
    },
    methods: {
        // 校验本地 token 是否有效，有效则自动进入
        async checkToken() {
            const token = getStorage('token');
            if (!token) return;

            try {
                const res = await request({
                    url: '/api/verify-token',
                    method: 'GET',
                });
                if (res.data.code === 200) {
                    const userData = res.data.data;
                    setStorage('userPhone', userData.phoneNumber);
                    setStorage('userId', userData.userId);
                    setStorage('hasCompletedGuide', userData.hasCompletedGuide);
                    setStorage('hasCompletedAssessment', userData.hasCompletedAssessment);
                    // 有未完成的引导先走引导页
                    if (!userData.hasCompletedGuide) {
                        this.$router.replace('/guide');
                        return;
                    }
                    // 检查是否有进行中的会话，有则直接进首页恢复
                    try {
                        const convRes = await request({
                            url: '/api/current-conversation',
                            method: 'GET',
                        });
                        if (convRes.data.code === 200 && convRes.data.data.conversationId) {
                            this.$router.replace('/chat');
                            return;
                        }
                    } catch (e) {
                        console.log('检查会话失败，继续弹窗:', e);
                    }
                    // 无进行中会话，弹窗让用户选择
                    this.handleLoginSuccess();
                } else {
                    this.clearAuthStorage();
                }
            } catch (err) {
                console.error('验证token失败:', err);
                this.clearAuthStorage();
            }
        },
        // 清除认证相关本地存储
        clearAuthStorage() {
            removeStorage('token');
            removeStorage('userPhone');
            removeStorage('userId');
            removeStorage('hasCompletedGuide');
            removeStorage('hasCompletedAssessment');
            removeStorage('userPreferences');
        },
        // 登录成功后处理
        handleLoginSuccess() {
            this.pendingUserData = {};
            this.hasCompletedAssessment = getStorage('hasCompletedAssessment') || false;
            this.showAssessmentModal = true;
        },

        // 登录后跳转（正常流程：未引导则走引导页）
        navigateAfterLogin() {
            if (getStorage('hasCompletedGuide')) {
                this.$router.replace('/chat');
            } else {
                this.$router.replace('/guide');
            }
        },

        // 直接聊天（默认选项）
        goToChat() {
            this.showAssessmentModal = false;
            if (this.pendingUserData) {
                // 根据引导状态跳转首页或引导页
                this.navigateAfterLogin();
            }
        },

        // 开始二筛评估：跳过引导页，直接进入首页启动评估
        goToAssessment() {
            const startAssessment = () => {
                this.showAssessmentModal = false;
                // 保存评估标记，首页 created 检测到后启动评估模式
                setStorage('needAssessment', true);
                // 标记引导已完成，避免再次进入引导页
                setStorage('hasCompletedGuide', true);
                if (this.pendingUserData) {
                    this.$router.replace('/chat');
                }
            };

            // 如果已完成二筛评估，提示用户（Vant Dialog 确认框）
            if (this.hasCompletedAssessment) {
                this.$dialog
                    .confirm({
                        title: '提示',
                        message:
                            '检测到您已生成过心理评估报告，重新进行评估将会覆盖原有报告。是否继续？',
                        confirmButtonText: '继续评估',
                        cancelButtonText: '取消',
                    })
                    .then(() => {
                        startAssessment();
                    })
                    .catch(() => {});
            } else {
                startAssessment();
            }
        },

        // 手动登录
        async handleLogin() {
            if (!this.canLogin || this.isLoggingIn) return;
            this.isLoggingIn = true;
            try {
                const res = await request({
                    url: '/api/phone-login',
                    method: 'POST',
                    data: { phoneNumber: this.phoneNumber.trim() },
                    needAuth: false,
                });
                if (res.data.code === 200) {
                    const userData = res.data.data;
                    setStorage('userPhone', userData.phoneNumber);
                    setStorage('userId', userData.userId);
                    setStorage('token', userData.token);
                    setStorage('hasCompletedGuide', userData.hasCompletedGuide);
                    setStorage('hasCompletedAssessment', userData.hasCompletedAssessment);

                    setTimeout(() => {
                        this.handleLoginSuccess(userData);
                    }, 800);
                } else {
                    this.$dialog.alert({
                        title: '提示',
                        message: res.data.message || '登录失败',
                    });
                }
            } catch (e) {
                this.$toast('网络错误');
            } finally {
                this.isLoggingIn = false;
            }
        },

        // 显示协议弹窗
        showAgreement(type) {
            this.agreementTitle = type === 'user' ? '用户协议' : '隐私政策';
            this.agreementContent =
                type === 'user'
                    ? '欢迎使用AI心理咨询服务平台！本服务由AI疗愈助手提供，为用户提供专业的心理健康咨询服务。请提供真实准确的个人信息，AI咨询建议仅供参考，不能替代专业医疗诊断。'
                    : '重视您的隐私，将严格按照法律法规保护您的个人信息。我们可能收集手机号码、设备信息、使用记录，用于提供和优化服务。';
            this.showAgreementModal = true;
        },
    },
};
</script>

<style lang="scss" scoped>
.login-page {
    width: 100vw;
    height: 100vh;
    // 暖色渐变背景，与首页风格统一
    background: linear-gradient(160deg, #fff5ee 0%, #ffe8d6 30%, #ffd6c0 60%, #ffcad4 100%);
    position: relative;
    overflow: hidden;
}

/* ==================== 启动动画 ==================== */
.splash-screen {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    background: linear-gradient(160deg, #fff5ee 0%, #ffe8d6 50%, #ffcad4 100%);
    z-index: 100;
    animation: splashFadeOut 0.5s ease 0.5s forwards;
}

.splash-bg {
    position: absolute;
    width: 100%;
    height: 100%;
    overflow: hidden;
    pointer-events: none;
}

.splash-circle {
    position: absolute;
    border-radius: 50%;
    opacity: 0.3;
}

.splash-circle-1 {
    width: 400rpx;
    height: 400rpx;
    top: -150rpx;
    right: -120rpx;
    background: radial-gradient(circle, #ffb38a 0%, #ff8c61 100%);
    animation: drift 20s ease-in-out infinite;
}

.splash-circle-2 {
    width: 280rpx;
    height: 280rpx;
    bottom: -80rpx;
    left: -80rpx;
    background: radial-gradient(circle, #ffd6c0 0%, #ffb088 100%);
    animation: drift 25s ease-in-out infinite;
    animation-delay: -5s;
}

.splash-circle-3 {
    width: 180rpx;
    height: 180rpx;
    top: 60%;
    right: -60rpx;
    background: radial-gradient(circle, #ffb6c1 0%, #ff8fa3 100%);
    animation: drift 18s ease-in-out infinite;
    animation-delay: -10s;
}

.splash-circle-4 {
    width: 120rpx;
    height: 120rpx;
    top: 20%;
    left: 10%;
    background: radial-gradient(circle, #ffe0b2 0%, #ffcc80 100%);
    animation: drift 22s ease-in-out infinite;
    animation-delay: -3s;
}

.splash-circle-5 {
    width: 80rpx;
    height: 80rpx;
    bottom: 30%;
    right: 20%;
    background: radial-gradient(circle, #f8bbd0 0%, #f48fb1 100%);
    animation: drift 15s ease-in-out infinite;
    animation-delay: -8s;
}

@keyframes drift {
    0%,
    100% {
        transform: translate(0, 0) rotate(0deg);
    }
    25% {
        transform: translate(10rpx, -15rpx) rotate(5deg);
    }
    50% {
        transform: translate(-5rpx, 10rpx) rotate(-3deg);
    }
    75% {
        transform: translate(-10rpx, -5rpx) rotate(3deg);
    }
}

@keyframes splashFadeOut {
    to {
        opacity: 0;
        visibility: hidden;
        transform: scale(1.05);
    }
}

.splash-logo {
    text-align: center;
}

.logo-sub {
    display: block;
    font-size: 26rpx;
    color: #b07060;
    margin-top: 20rpx;
    font-weight: 400;
    letter-spacing: 4rpx;
    animation: logoSlideUp 0.7s ease;
}

@keyframes logoSlideUp {
    from {
        opacity: 0;
        transform: translateY(30rpx);
    }
    to {
        opacity: 1;
        transform: translateY(0);
    }
}

/* 加载圆点（原 gap 改用相邻 margin，保证兼容性） */
.splash-loading {
    display: flex;
    justify-content: center;
    margin-top: 80rpx;
}

.loading-dot {
    width: 14rpx;
    height: 14rpx;
    background: #e8735a;
    border-radius: 50%;
    animation: bounce 1.4s ease-in-out infinite;

    & + .loading-dot {
        margin-left: 16rpx;
    }
}

.loading-dot:nth-child(1) {
    animation-delay: 0s;
}
.loading-dot:nth-child(2) {
    animation-delay: 0.2s;
}
.loading-dot:nth-child(3) {
    animation-delay: 0.4s;
}

@keyframes bounce {
    0%,
    80%,
    100% {
        transform: scale(0.6);
    }
    40% {
        transform: scale(1);
    }
}

/* ==================== 登录区域 ==================== */
.login-container {
    width: 100%;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 40rpx;
    box-sizing: border-box;
}

// 背景装饰圆
.bg-decoration {
    position: absolute;
    width: 100%;
    height: 100%;
    overflow: hidden;
    pointer-events: none;
}

.bg-circle {
    position: absolute;
    border-radius: 50%;
    opacity: 0.35;
}

.circle-1 {
    width: 300rpx;
    height: 300rpx;
    top: -100rpx;
    right: -80rpx;
    background: radial-gradient(circle, #ffb38a 0%, #ff9a6c 100%);
    animation: float 12s ease-in-out infinite;
}

.circle-2 {
    width: 200rpx;
    height: 200rpx;
    bottom: -50rpx;
    left: 8%;
    background: radial-gradient(circle, #ffd6c0 0%, #ffba8c 100%);
    animation: float 15s ease-in-out infinite;
    animation-delay: 3s;
}

.circle-3 {
    width: 140rpx;
    height: 140rpx;
    top: 35%;
    right: 6%;
    background: radial-gradient(circle, #ffb6c1 0%, #ff9aae 100%);
    animation: float 18s ease-in-out infinite;
    animation-delay: 5s;
}

.circle-4 {
    width: 100rpx;
    height: 100rpx;
    top: 15%;
    left: 12%;
    background: radial-gradient(circle, #ffe0b2 0%, #ffcc80 100%);
    animation: float 14s ease-in-out infinite;
    animation-delay: 2s;
}

@keyframes float {
    0%,
    100% {
        transform: translateY(0) rotate(0deg);
    }
    50% {
        transform: translateY(-30rpx) rotate(180deg);
    }
}

/* ==================== 登录卡片 ==================== */
.login-card {
    width: 90%;
    max-width: 680rpx;
    background: rgba(255, 255, 255, 0.92);
    border-radius: 40rpx;
    box-shadow: 0 24rpx 64rpx rgba(255, 160, 120, 0.2), 0 12rpx 32rpx rgba(0, 0, 0, 0.06);
    animation: cardIn 0.6s ease;
    position: relative;
    z-index: 10;
    overflow: hidden;
}

.login-card::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 10rpx;
    background: linear-gradient(90deg, #ffb088 0%, #e8735a 50%, #d4604e 100%);
}

@keyframes cardIn {
    from {
        opacity: 0;
        transform: translateY(60rpx) scale(0.96);
    }
    to {
        opacity: 1;
        transform: translateY(0) scale(1);
    }
}

.card-header {
    padding: 56rpx 48rpx 44rpx;
    text-align: center;
    position: relative;
}

.card-header::after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 48rpx;
    right: 48rpx;
    height: 1rpx;
    background: linear-gradient(90deg, transparent, #f0d5c8, transparent);
}

// 品牌爱心图标
.brand-icon {
    width: 88rpx;
    height: 88rpx;
    margin: 0 auto 24rpx;
    background: linear-gradient(135deg, #ff9a6c 0%, #e8735a 100%);
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    box-shadow: 0 8rpx 24rpx rgba(232, 115, 90, 0.3);

    .icon-heart {
        font-size: 40rpx;
        color: #ffffff;
    }
}

.card-title {
    font-size: 44rpx;
    font-weight: 700;
    color: #3d2c2c;
    letter-spacing: 4rpx;
    display: block;
}

.card-desc {
    font-size: 28rpx;
    color: #a08070;
    margin-top: 16rpx;
    font-weight: 400;
    letter-spacing: 1rpx;
}

.card-body {
    padding: 40rpx 48rpx 48rpx;
}

/* ==================== 手动输入 ==================== */
.manual-input {
    width: 100%;
    animation: slideUp 0.35s ease;
}

@keyframes slideUp {
    from {
        opacity: 0;
        transform: translateY(25rpx);
    }
    to {
        opacity: 1;
        transform: translateY(0);
    }
}

.input-wrapper {
    display: flex;
    align-items: center;
    height: 96rpx;
    background: #faf6f4;
    border-radius: 16rpx;
    margin-bottom: 28rpx;
    box-sizing: border-box;
    border: 2rpx solid #f0ddd5;
    transition: all 0.3s ease;
    overflow: hidden;

    &:focus-within {
        border-color: #e8735a;
        background: #fff;
        box-shadow: 0 0 0 6rpx rgba(232, 115, 90, 0.12);
    }
}

.input-prefix {
    padding: 0 20rpx 0 28rpx;
    font-size: 30rpx;
    color: #888;
    border-right: 2rpx solid #f0ddd5;
    margin-right: 16rpx;
    line-height: 96rpx;
}

.phone-input {
    flex: 1;
    height: 96rpx;
    font-size: 30rpx;
    color: #3d2c2c;
    padding-right: 28rpx;
    border: none;
    outline: none;
    background: transparent;
}

.phone-input::placeholder {
    color: #c0a898;
}

.submit-btn {
    width: 100%;
    height: 96rpx;
    padding: 0;
    background: linear-gradient(135deg, #ff9a6c 0%, #e8735a 100%);
    border-radius: 48rpx;
    border: none;
    color: #fff;
    font-size: 32rpx;
    font-weight: 500;
    display: flex;
    align-items: center;
    justify-content: center;
    box-shadow: 0 8rpx 24rpx rgba(232, 115, 90, 0.35);
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    position: relative;
    overflow: hidden;
}

.submit-btn::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.2), transparent);
    transform: translateX(-100%);
    transition: transform 0.6s ease;
}

.submit-btn:active:not(.disabled) {
    transform: scale(0.97);
    box-shadow: 0 4rpx 16rpx rgba(232, 115, 90, 0.25);
}

.submit-btn:active:not(.disabled)::after {
    transform: translateX(100%);
}

.submit-btn.disabled {
    background: linear-gradient(135deg, #e0cfc8 0%, #d4c0b8 100%);
    box-shadow: none;
}

.submit-btn.loading {
    opacity: 0.75;
}

/* ==================== 卡片底部 ==================== */
.card-footer {
    text-align: center;
    padding: 0 40rpx 40rpx;
}

.agreement-text {
    font-size: 22rpx;
    color: #b0a098;
}

.agreement-link {
    font-size: 22rpx;
    color: #e8735a;
}

/* ==================== 协议弹窗 ==================== */
.modal-mask {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.5);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 200;
    padding: 80rpx 60rpx;
}

.modal-content {
    width: 100%;
    height: 60vh;
    background: #fff;
    border-radius: 24rpx;
    overflow: hidden;
    display: flex;
    flex-direction: column;
}

.modal-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 28rpx 32rpx;
    border-bottom: 1rpx solid #f0e8e4;
    flex-shrink: 0;
}

.modal-title {
    font-size: 30rpx;
    font-weight: 600;
    color: #3d2c2c;
}

.modal-close {
    font-size: 36rpx;
    color: #a09090;
    line-height: 1;
    padding: 8rpx;
}

.modal-body {
    flex: 1;
    overflow-y: auto;
    padding: 28rpx 32rpx;
    box-sizing: border-box;
    -webkit-overflow-scrolling: touch;
}

.modal-text {
    font-size: 26rpx;
    color: #666;
    line-height: 1.8;
    word-break: break-all;
}

/* ==================== 二筛评估弹窗 ==================== */
.assessment-mask {
    /* iPad 兼容性：确保遮罩层可点击 */
    pointer-events: auto;
}

.assessment-modal {
    width: 90%;
    max-width: 600rpx;
    background: #fff;
    border-radius: 24rpx;
    overflow: hidden;
}

.assessment-header {
    padding: 48rpx 40rpx 32rpx;
    text-align: center;
}

.assessment-title {
    font-size: 36rpx;
    font-weight: 600;
    color: #3d2c2c;
    display: block;
    margin-bottom: 20rpx;
}

.assessment-desc {
    font-size: 28rpx;
    color: #888;
    display: block;
    line-height: 1.6;
}

.assessment-body {
    padding: 0 40rpx 48rpx;
}

.assessment-btn {
    height: 120rpx;
    border-radius: 24rpx;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    margin-bottom: 24rpx;
    transition: all 0.3s ease;

    &:last-child {
        margin-bottom: 0;
    }

    &:active {
        transform: scale(0.97);
    }

    .btn-main {
        font-size: 32rpx;
        font-weight: 600;
        margin-bottom: 6rpx;
    }

    .btn-sub {
        font-size: 24rpx;
        font-weight: 400;
        opacity: 0.8;
    }

    &.primary {
        background: linear-gradient(135deg, #ff9a6c 0%, #e8735a 100%);
        box-shadow: 0 8rpx 24rpx rgba(232, 115, 90, 0.35);

        .btn-main,
        .btn-sub {
            color: #fff;
        }
    }

    &.secondary {
        background: #f5f0ee;

        .btn-main {
            color: #666;
        }

        .btn-sub {
            color: #999;
        }
    }
}
</style>
